import { useState, useEffect } from 'react';
import { getPermissionDefaults, getUserPermissions, updateUserPermissions } from '../lib/api';
import { Shield, Check, X as XIcon, Plus, Minus, User, FloppyDisk, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const PERM_LABELS = {
    'view_all_quotes': 'View all quotes',
    'view_own_quotes': 'View own quotes only',
    'create_quotes': 'Create quotes',
    'edit_all_quotes': 'Edit all quotes',
    'edit_own_quotes': 'Edit own quotes only',
    'delete_quotes': 'Delete quotes',
    'override_price': 'Override price',
    'export_quotes': 'Export quotes (PDF/CSV)',
    'duplicate_quotes': 'Duplicate quotes',
    'view_products': 'View products',
    'edit_products': 'Edit products',
    'export_products': 'Export products (CSV/Excel)',
    'view_custom_products': 'View custom products',
    'create_custom_products': 'Create custom products',
    'edit_custom_products': 'Edit custom products',
    'delete_custom_products': 'Delete custom products',
    'view_customers': 'View customers',
    'create_customers': 'Create customers',
    'edit_customers': 'Edit customers',
    'delete_customers': 'Deactivate customers',
    'export_customers': 'Export customers (CSV/Excel)',
    'manage_customer_migration': 'Customer migration tool',
    'view_challans': 'View delivery challans',
    'create_challans': 'Create challans',
    'edit_challans': 'Edit challans',
    'delete_challans': 'Delete challans',
    'export_challans': 'Export challans (PDF/CSV)',
    'duplicate_challans': 'Duplicate challans',
    'use_sheet_optimizer': 'Use Sheet Optimizer',
    'view_dashboard': 'View dashboard',
    'manage_users': 'Manage users',
    'import_data': 'Import CSV data',
    'import_images': 'Import product images',
    'manage_unmatched_images': 'Match/dismiss unmatched images',
    'manage_config': 'Manage system config lists',
    'view_costing': 'View costing data',
    'manage_categories': 'Manage categories',
};

const PERM_GROUPS = {
    'Quotations': ['view_all_quotes', 'view_own_quotes', 'create_quotes', 'edit_all_quotes', 'edit_own_quotes', 'delete_quotes', 'override_price', 'export_quotes', 'duplicate_quotes'],
    'Products': ['view_products', 'edit_products', 'export_products', 'view_costing'],
    'Custom Products': ['view_custom_products', 'create_custom_products', 'edit_custom_products', 'delete_custom_products'],
    'Customers': ['view_customers', 'create_customers', 'edit_customers', 'delete_customers', 'export_customers', 'manage_customer_migration'],
    'Delivery Challans': ['view_challans', 'create_challans', 'edit_challans', 'delete_challans', 'export_challans', 'duplicate_challans'],
    'Tools': ['use_sheet_optimizer', 'view_dashboard'],
    'Data Management': ['import_data', 'import_images', 'manage_unmatched_images', 'manage_categories', 'manage_config'],
    'Administration': ['manage_users'],
};

export default function PermissionSettings() {
    const [users, setUsers] = useState([]);
    const [defaults, setDefaults] = useState({ all_permissions: [], role_defaults: {} });
    const [loading, setLoading] = useState(true);
    const [editUser, setEditUser] = useState(null);
    const [editGrant, setEditGrant] = useState([]);
    const [editRevoke, setEditRevoke] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [defRes, usrRes] = await Promise.all([getPermissionDefaults(), getUserPermissions()]);
            setDefaults(defRes.data);
            setUsers(usrRes.data.users);
        } catch { toast.error('Failed to load'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openEdit = (u) => {
        setEditUser(u);
        setEditGrant(u.permission_overrides?.grant || []);
        setEditRevoke(u.permission_overrides?.revoke || []);
    };

    const toggleGrant = (perm) => {
        setEditGrant(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
        setEditRevoke(prev => prev.filter(p => p !== perm));
    };

    const toggleRevoke = (perm) => {
        setEditRevoke(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
        setEditGrant(prev => prev.filter(p => p !== perm));
    };

    const handleSave = async () => {
        if (!editUser) return;
        setSaving(true);
        try {
            await updateUserPermissions(editUser.id, { grant: editGrant, revoke: editRevoke });
            toast.success('Permissions updated');
            setEditUser(null);
            load();
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;

    return (
        <div className="space-y-6" data-testid="permission-settings">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Permission Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">Manage role defaults and per-user permission overrides</p>
            </div>

            {/* Role Defaults Summary */}
            <div className="bg-card border border-border rounded-lg p-5">
                <h3 className="font-semibold text-sm mb-3">Role Defaults</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(defaults.role_defaults).map(([role, perms]) => (
                        <div key={role} className="border border-border rounded-md p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <Shield size={14} className="text-primary" />
                                <span className="text-sm font-semibold capitalize">{role}</span>
                                <Badge variant="outline" className="text-[10px] ml-auto">{perms.length}</Badge>
                            </div>
                            <div className="space-y-0.5">
                                {perms.map(p => (
                                    <div key={p} className="text-[11px] text-muted-foreground flex items-center gap-1">
                                        <Check size={10} className="text-emerald-500 flex-shrink-0" />
                                        {PERM_LABELS[p] || p}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* User Overrides */}
            <div className="bg-card border border-border rounded-lg">
                <div className="px-5 py-3.5 border-b border-border">
                    <h3 className="font-semibold text-sm">User Permission Overrides</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Override role defaults for specific users</p>
                </div>
                <div className="divide-y divide-border">
                    {users.map(u => {
                        const hasOverrides = (u.permission_overrides?.grant?.length > 0) || (u.permission_overrides?.revoke?.length > 0);
                        return (
                            <div key={u.id} className="px-5 py-3 flex items-center justify-between" data-testid={`perm-user-${u.id}`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <User size={14} className="text-primary" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium">{u.name}</div>
                                        <div className="text-xs text-muted-foreground">{u.email} · <span className="capitalize">{u.role}</span></div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {hasOverrides && (
                                        <div className="flex gap-1">
                                            {(u.permission_overrides?.grant?.length > 0) && (
                                                <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200">
                                                    <Plus size={9} className="mr-0.5" />{u.permission_overrides.grant.length} granted
                                                </Badge>
                                            )}
                                            {(u.permission_overrides?.revoke?.length > 0) && (
                                                <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">
                                                    <Minus size={9} className="mr-0.5" />{u.permission_overrides.revoke.length} revoked
                                                </Badge>
                                            )}
                                        </div>
                                    )}
                                    <Badge variant="outline" className="text-[10px]">{u.effective_permissions.length} perms</Badge>
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openEdit(u)} data-testid={`edit-perms-${u.id}`}>
                                        Edit Overrides
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Edit Overrides Dialog */}
            <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Permission Overrides — {editUser?.name}</DialogTitle>
                        <DialogDescription>
                            Role: <span className="capitalize font-medium">{editUser?.role}</span>. Grant extra permissions or revoke default ones.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {Object.entries(PERM_GROUPS).map(([group, perms]) => {
                            const roleDefaults = defaults.role_defaults[editUser?.role] || [];
                            return (
                                <div key={group}>
                                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group}</h4>
                                    <div className="space-y-1">
                                        {perms.map(perm => {
                                            const isDefault = roleDefaults.includes(perm);
                                            const isGranted = editGrant.includes(perm);
                                            const isRevoked = editRevoke.includes(perm);
                                            const effective = (isDefault || isGranted) && !isRevoked;

                                            return (
                                                <div key={perm} className={`flex items-center justify-between px-3 py-2 rounded-md text-sm ${effective ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : 'bg-muted/30'}`}>
                                                    <div className="flex items-center gap-2">
                                                        {effective ? <Check size={14} className="text-emerald-600" /> : <XIcon size={12} className="text-muted-foreground/40" />}
                                                        <span className={effective ? '' : 'text-muted-foreground'}>{PERM_LABELS[perm] || perm}</span>
                                                        {isDefault && <Badge variant="outline" className="text-[9px] h-4">default</Badge>}
                                                    </div>
                                                    <div className="flex gap-1">
                                                        {!isDefault && (
                                                            <button
                                                                className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${isGranted ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-muted text-muted-foreground hover:bg-emerald-50'}`}
                                                                onClick={() => toggleGrant(perm)}
                                                                data-testid={`grant-${perm}`}
                                                            >
                                                                <Plus size={10} className="inline" /> Grant
                                                            </button>
                                                        )}
                                                        {isDefault && (
                                                            <button
                                                                className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${isRevoked ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' : 'bg-muted text-muted-foreground hover:bg-red-50'}`}
                                                                onClick={() => toggleRevoke(perm)}
                                                                data-testid={`revoke-${perm}`}
                                                            >
                                                                <Minus size={10} className="inline" /> Revoke
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                        <div className="flex gap-2 pt-2">
                            <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-perms-btn">
                                <FloppyDisk size={16} className="mr-2" />{saving ? 'Saving...' : 'Save Overrides'}
                            </Button>
                            <Button variant="outline" onClick={() => { setEditGrant([]); setEditRevoke([]); }} data-testid="reset-overrides-btn">
                                <ArrowsClockwise size={14} className="mr-1" /> Reset
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
